import { useSearchDoctorsByUserQuery } from '@/hooks/telemedicine/telemedicine.gql.impl';
import {
  EBookingAvailability,
  EBookingScheduleType,
  NonF2fBookingInfo,
  NonF2fBookingSchedule,
  NonF2fDoctor,
  NonF2fDoctorReviewInfo,
} from '@/models/doctor/telemedicine';
import { ECategoryType } from '@/services/doctor/DiagnosisCategoryService';
import {
  BookingAvailability,
  DiagnosisContactType,
  DiagnosisPriceType,
  DoctorSortType,
  MedicalCategoryType,
  SearchDoctorsByUserQuery,
} from '@/types/bff/gql-schema';
import { QueryClient } from '@tanstack/react-query';

const parseCategoryType = (categoryType: ECategoryType) => {
  switch (categoryType) {
    case ECategoryType.Department:
      return MedicalCategoryType.Department;
    case ECategoryType.Treatment:
      return MedicalCategoryType.Treatment;
  }
};

const parseBookingAvailability = (bookingAvailability: BookingAvailability): EBookingAvailability => {
  switch (bookingAvailability) {
    case BookingAvailability.NotBookable:
      return EBookingAvailability.NotBookable;
    case BookingAvailability.TodayBookable:
      return EBookingAvailability.TodayBookable;
    case BookingAvailability.TomorrowBookable:
      return EBookingAvailability.TomorrowBookable;
  }
};

const parseDoctorByUser = (
  data: SearchDoctorsByUserQuery
): {
  doctors: NonF2fDoctor[];
  totalCount: number;
} => {
  return {
    totalCount: data.searchDoctorsByUser.totalCount,
    doctors: data.searchDoctorsByUser.items.map((doctor) => {
      return new NonF2fDoctor(
        doctor.hospitalName,
        doctor.publicId,
        doctor.name,
        doctor.medicineType,
        doctor.profileUrl ?? null,
        doctor.readyForVideoCall,
        doctor.professionalDepartment?.name ?? null,
        new NonF2fDoctorReviewInfo(doctor.reviewAverageRating, doctor.reviewCount),
        new NonF2fBookingInfo(parseBookingAvailability(doctor.bookingInfo.bookingAvailability), [
          new NonF2fBookingSchedule(
            doctor.bookingInfo.todayBookable,
            doctor.bookingInfo.todaySchedule,
            EBookingScheduleType.Today
          ),
          new NonF2fBookingSchedule(
            doctor.bookingInfo.tomorrowBookable,
            doctor.bookingInfo.tomorrowSchedule,
            EBookingScheduleType.Tomorrow
          ),
        ]),
        null
      );
    }),
  };
};

const SEARCH_DOCTORS_LIMIT = 10;

const DEFAULT_OPTIONS = {
  limit: SEARCH_DOCTORS_LIMIT,
  sortType: DoctorSortType.Default,
  contactType: DiagnosisContactType.NonF2F,
  priceType: DiagnosisPriceType.DiagnosisOnly,
};

const useSearchDoctorsByUser = (categoryType: ECategoryType, categoryId: string, page: number) => {
  const {
    data = {
      doctors: [],
      totalCount: 0,
    },
  } = useSearchDoctorsByUserQuery(
    {
      request: {
        medicalCategory: {
          category: parseCategoryType(categoryType),
          id: categoryId,
        },
        page: page,
        ...DEFAULT_OPTIONS,
      },
    },
    {
      enabled: !!categoryId && !!categoryType && !!page,
      select: parseDoctorByUser,
    }
  );

  return {
    nonF2fDoctors: data.doctors,
    totalPage:
      Math.floor(data.totalCount / SEARCH_DOCTORS_LIMIT) + (data.totalCount % SEARCH_DOCTORS_LIMIT > 0 ? 1 : 0),
  };
};

export default useSearchDoctorsByUser;

useSearchDoctorsByUser.getKey = (categoryType: ECategoryType, categoryId: string, page: number) => {
  return useSearchDoctorsByUserQuery.getKey({
    request: {
      medicalCategory: {
        category: parseCategoryType(categoryType),
        id: categoryId,
      },
      page: page,
      ...DEFAULT_OPTIONS,
    },
  });
};

useSearchDoctorsByUser.prefetchQuery = async (
  queryClient: QueryClient,
  categoryType: ECategoryType,
  categoryId: string,
  page: number
) => {
  await queryClient.prefetchQuery(
    useSearchDoctorsByUser.getKey(categoryType, categoryId, page),
    useSearchDoctorsByUserQuery.fetcher({
      request: {
        medicalCategory: {
          category: parseCategoryType(categoryType),
          id: categoryId,
        },
        page: page,
        ...DEFAULT_OPTIONS,
      },
    })
  );
};
